/**
 * キャラクター用BGMビート連動演出システム
 * 高度な感情・動作制御による音楽シンクロ演出
 *
 * Note: Uses Web Animations API for DOM animations
 * No GSAP dependency - pure browser APIs for better performance
 */

import { logger } from '@/utils/logger.js';

/**
 * Easing functions for animations
 */
const easings = {
  easeOutPower2: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeInOutPower2: 'cubic-bezier(0.65, 0, 0.35, 1)',
  elasticOut: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
};

/**
 * Animate element using Web Animations API with chaining support
 */
function animateWithChain(
  element: Element,
  keyframes: Keyframe[],
  options: KeyframeAnimationOptions
): Animation {
  return element.animate(keyframes, options);
}

/**
 * Create a sequential animation chain (timeline-like behavior)
 */
async function animateSequence(
  element: Element,
  sequences: Array<{ keyframes: Keyframe[]; options: KeyframeAnimationOptions }>
): Promise<void> {
  for (const seq of sequences) {
    const anim = element.animate(seq.keyframes, seq.options);
    await anim.finished;
  }
}

/**
 * Emotion boost parameters for beat effects (16次元感情システム互換)
 */
interface EmotionBoost {
    energy?: number;
    playfulness?: number;
    happiness?: number;
    trust?: number;        // comfort → trust
    surprise?: number;
}

interface MoodPattern {
    kickActions: string[];
    emotionBoost: EmotionBoost;
    blinkPattern: string;
    bodyPattern: string;
    scaleMultiplier: number;
    rotationRange: number;
}

interface LastEffectTime {
    kick: number;
    emotion: number;
    action: number;
    scale: number;
}

interface SpecialEffectState {
    isInSpecialMode: boolean;
    specialModeEndTime: number;
    currentSpecialEffect: string | null;
}

interface MusicAnalysis {
    tempo?: number;
    energy?: number;
    spectralCentroid?: number;
    dynamicRange?: number;
}

interface TokchanActionPayload {
    action: string;
    intensity: number;
    options?: Record<string, unknown>;
}

interface TokchanController {
    performAction(payload: TokchanActionPayload): void;
    setEmotions(emotions: EmotionBoost): void;
    blinkSystem?: unknown;
    setBlinkPattern?(pattern: string): void;
    bodyAnimationSystem?: unknown;
    setBodyPattern?(pattern: string, flag: boolean): void;
}

class CharacterBeatEffects {
    private controller: TokchanController;
    private isActive: boolean;
    private moodPatterns: Record<string, MoodPattern>;
    private lastEffectTime: LastEffectTime;
    private intensityHistory: number[];
    private maxHistoryLength: number;
    private comboCounter: number;
    private maxCombo: number;
    private comboDecayTime: number;
    private specialEffectState: SpecialEffectState;
    private customPatterns: Map<string, MoodPattern>;
    private lastUltimateEffect: number;

    constructor(tokchanController: TokchanController) {
        this.controller = tokchanController;
        this.isActive = false;
        
        // 演出パターン設定（TokchanControllerの利用可能アクションに対応）
        // 利用可能: happy, sad, excited, calm, idle, surprised, curious, grateful,
        //          confused, shy, anxious, loving, angry, sleepy, normalSpin, normalWiggle
        this.moodPatterns = {
            intense: {
                kickActions: ['excited', 'angry', 'surprised', 'normalSpin'],
                emotionBoost: { energy: 0.9, playfulness: 0.8, happiness: 0.7 },
                blinkPattern: 'excited',
                bodyPattern: 'energetic',
                scaleMultiplier: 1.3,
                rotationRange: 15
            },
            energetic: {
                kickActions: ['happy', 'excited', 'normalWiggle', 'surprised'],
                emotionBoost: { energy: 0.7, playfulness: 0.6, happiness: 0.8 },
                blinkPattern: 'excited',
                bodyPattern: 'happy',
                scaleMultiplier: 1.2,
                rotationRange: 10
            },
            normal: {
                kickActions: ['happy', 'normalWiggle', 'excited', 'curious', 'grateful', 'normalSpin'],
                emotionBoost: { energy: 0.6, happiness: 0.6 },
                blinkPattern: 'natural',
                bodyPattern: 'idle',
                scaleMultiplier: 1.1,
                rotationRange: 5
            },
            chill: {
                kickActions: ['calm', 'idle', 'sleepy', 'loving'],
                emotionBoost: { trust: 0.7, happiness: 0.5 },
                blinkPattern: 'sleepy',
                bodyPattern: 'calm',
                scaleMultiplier: 1.05,
                rotationRange: 3
            }
        };
        
        // 演出タイミング制御
        this.lastEffectTime = {
            kick: 0,
            emotion: 0,
            action: 0,
            scale: 0
        };
        
        // 演出インテンシティ履歴（スムージング用）
        this.intensityHistory = [];
        this.maxHistoryLength = 5;
        
        // 連続演出管理
        this.comboCounter = 0;
        this.maxCombo = 8;
        this.comboDecayTime = 2000; // 2秒
        
        // 特殊演出状態
        this.specialEffectState = {
            isInSpecialMode: false,
            specialModeEndTime: 0,
            currentSpecialEffect: null
        };
        
        // カスタム演出パターン
        this.customPatterns = new Map();

        // Ultimate effect tracking
        this.lastUltimateEffect = 0;

        // Character Beat Effects initialization log removed for console noise reduction
    }
    
    /**
     * キックビート演出メイン処理
     */
    onKickDetected(intensity: number, mood: string, musicAnalysis: MusicAnalysis) {
        logger.info(`🎭 CharacterBeatEffects.onKickDetected called - intensity: ${intensity.toFixed(2)}, mood: ${mood}, hasController: ${!!this.controller}, isActive: ${this.isActive}`);
        if (!this.controller || !this.isActive) {
            logger.warn(`🎭 CharacterBeatEffects skipping - controller: ${!!this.controller}, isActive: ${this.isActive}`);
            return;
        }

        const now = Date.now();
        const pattern = this.moodPatterns[mood] || this.moodPatterns.normal;

        if (!pattern) {
            logger.warn(`Invalid mood pattern: ${mood}`);
            return;
        }

        // インテンシティ履歴更新
        this.updateIntensityHistory(intensity);
        const smoothedIntensity = this.getSmoothedIntensity();

        // コンボ更新
        this.updateCombo(now);

        // 演出実行
        this.executeKickEffects(smoothedIntensity, pattern, musicAnalysis, now);

        // 特殊演出トリガー判定
        this.checkSpecialEffectTriggers(smoothedIntensity, mood, now);
    }
    
    /**
     * インテンシティ履歴更新・スムージング
     */
    updateIntensityHistory(intensity: number) {
        this.intensityHistory.push(intensity);
        if (this.intensityHistory.length > this.maxHistoryLength) {
            this.intensityHistory.shift();
        }
    }
    
    /**
     * スムージングされたインテンシティ取得
     */
    getSmoothedIntensity() {
        if (this.intensityHistory.length === 0) return 0;

        // 重み付き平均（新しいものほど重い）
        let weightedSum = 0;
        let weightSum = 0;

        for (let i = 0; i < this.intensityHistory.length; i++) {
            const intensity = this.intensityHistory[i];
            if (typeof intensity === 'number' && !isNaN(intensity)) {
                const weight = (i + 1) / this.intensityHistory.length;
                weightedSum += intensity * weight;
                weightSum += weight;
            }
        }

        return weightSum > 0 ? weightedSum / weightSum : 0;
    }
    
    /**
     * コンボシステム更新
     */
    updateCombo(now: number) {
        // コンボ継続判定
        if (now - this.lastEffectTime.kick < this.comboDecayTime) {
            this.comboCounter = Math.min(this.comboCounter + 1, this.maxCombo);
        } else {
            this.comboCounter = 1;
        }
        
        this.lastEffectTime.kick = now;
    }
    
    /**
     * キックエフェクト実行
     */
    executeKickEffects(intensity: number, pattern: MoodPattern, musicAnalysis: MusicAnalysis, timestamp: number) {
        // 1. 基本アクション（確率的選択）
        this.executeRandomAction(intensity, pattern, timestamp);
        
        // 2. 感情パラメータ調整
        this.adjustEmotionalResponse(intensity, pattern, musicAnalysis, timestamp);
        
        // 3. 物理的変化（スケール、回転）
        this.applyPhysicalEffects(intensity, pattern, timestamp);
        
        // 4. 表情・瞬き調整
        this.adjustFacialExpressions(intensity, pattern, timestamp);
        
        // 5. コンボ特殊効果
        this.applyComboEffects(intensity, pattern);
    }
    
    /**
     * ランダムアクション実行
     */
    executeRandomAction(intensity: number, pattern: MoodPattern, timestamp: number) {
        if (timestamp - this.lastEffectTime.action < 100) return; // 最低間隔を短縮
        
        const actions = pattern.kickActions;
        if (!actions || actions.length === 0) return;

        const selectedAction = actions[Math.floor(Math.random() * actions.length)];
        if (!selectedAction) return;

        // インテンシティベースの実行判定（確率を上げて多様性向上）
        const executionProbability = 0.6 + (intensity * 0.4);
        if (Math.random() > executionProbability) return;

        // コンボボーナス
        const comboMultiplier = 1 + (this.comboCounter - 1) * 0.1;
        const finalIntensity = Math.min(1.0, intensity * comboMultiplier);

        logger.info(`🎬 Executing action: ${selectedAction} with intensity: ${finalIntensity.toFixed(2)}`);
        this.controller.performAction({ action: selectedAction, intensity: finalIntensity });
        this.lastEffectTime.action = timestamp;
    }
    
    /**
     * 感情パラメータ調整
     * Note: TokchanControllerにはsetEmotionsメソッドがないため、
     * 感情に対応するアクションを実行することで表現する
     */
    adjustEmotionalResponse(_intensity: number, _pattern: MoodPattern, _musicAnalysis: MusicAnalysis, timestamp: number) {
        if (timestamp - this.lastEffectTime.emotion < 500) return; // 感情変化は0.5秒間隔

        // 感情表現はperformActionで既に行われているため、
        // ここでは追加のタイムスタンプ更新のみ
        this.lastEffectTime.emotion = timestamp;
    }
    
    /**
     * 物理的エフェクト適用
     */
    applyPhysicalEffects(intensity: number, pattern: MoodPattern, timestamp: number) {
        if (timestamp - this.lastEffectTime.scale < 150) return;

        // スケール脈動
        const baseScale = 1.0;
        const scaleBoost = (pattern.scaleMultiplier - 1.0) * intensity;
        let targetScale = baseScale + scaleBoost;

        // 回転振動
        const rotationAmount = pattern.rotationRange * intensity * (Math.random() - 0.5);

        // コンボエフェクト
        let comboDuration = 200; // ms
        let comboEasing = easings.easeOutPower2;

        if (this.comboCounter >= 6) {
            comboDuration = 300;
            comboEasing = easings.elasticOut;
            targetScale *= 1.1;
        }

        const characterElement = document.getElementById('tokchan-display') ||
                               document.querySelector('.character-container');

        if (characterElement) {
            // スケール＆回転アニメーション using Web Animations API
            animateSequence(characterElement, [
                {
                    keyframes: [
                        { transform: 'scale(1) rotate(0deg)' },
                        { transform: `scale(${targetScale}) rotate(${rotationAmount}deg)` }
                    ],
                    options: {
                        duration: comboDuration * 0.4,
                        easing: easings.easeOutPower2,
                        fill: 'forwards'
                    }
                },
                {
                    keyframes: [
                        { transform: `scale(${targetScale}) rotate(${rotationAmount}deg)` },
                        { transform: `scale(${baseScale}) rotate(0deg)` }
                    ],
                    options: {
                        duration: comboDuration * 0.6,
                        easing: comboEasing,
                        fill: 'forwards'
                    }
                }
            ]);
        }

        this.lastEffectTime.scale = timestamp;
    }
    
    /**
     * 表情・瞬き調整
     */
    adjustFacialExpressions(intensity: number, pattern: MoodPattern, _timestamp: number) {
        // 瞬きパターン調整 - BlinkSystemを直接操作
        const blinkSystem = this.controller.blinkSystem as { setBlinkPattern?: (pattern: string) => void } | undefined;
        if (blinkSystem?.setBlinkPattern) {
            // 高インテンシティ時の特殊瞬きパターン
            if (intensity > 0.8) {
                blinkSystem.setBlinkPattern('surprised');
            } else if (intensity > 0.5) {
                blinkSystem.setBlinkPattern(pattern.blinkPattern);
            }
        }

        // 身体アニメーションパターン - performActionで代替
        if (this.controller.bodyAnimationSystem && intensity > 0.7) {
            // bodyPatternに基づいてアクションを実行
            const bodyAction = pattern.bodyPattern === 'energetic' ? 'excited' :
                              pattern.bodyPattern === 'gentle' ? 'calm' : 'happy';
            this.controller.performAction({ action: bodyAction, intensity: intensity * 0.5 });
        }
    }
    
    /**
     * コンボ特殊効果
     */
    applyComboEffects(intensity: number, _pattern: MoodPattern) {
        if (this.comboCounter >= 4 && !this.specialEffectState.isInSpecialMode) {
            // 4コンボ以上で特殊演出モード
            this.triggerSpecialComboEffect(this.comboCounter, intensity);
        }
    }
    
    /**
     * 特殊コンボ演出
     */
    triggerSpecialComboEffect(combo: number, intensity: number) {
        const now = Date.now();
        const duration = 3000; // 3秒間

        this.specialEffectState = {
            isInSpecialMode: true,
            specialModeEndTime: now + duration,
            currentSpecialEffect: `combo_${combo}`
        };


        // 特殊演出実行
        const characterElement = document.getElementById('tokchan-display');
        if (characterElement) {
            // 輝きエフェクト using Web Animations API
            animateSequence(characterElement, [
                {
                    keyframes: [
                        { filter: 'brightness(1) saturate(1)' },
                        { filter: `brightness(${1.2 + intensity * 0.5}) saturate(${1.3 + intensity * 0.4})` }
                    ],
                    options: { duration: 200, fill: 'forwards' }
                },
                {
                    keyframes: [
                        { filter: `brightness(${1.2 + intensity * 0.5}) saturate(${1.3 + intensity * 0.4})` },
                        { filter: 'brightness(1) saturate(1)' }
                    ],
                    options: {
                        duration: duration - 200,
                        easing: easings.easeOutPower2,
                        fill: 'forwards'
                    }
                }
            ]);

            // 高速連続パルス
            const targetScale = 1.15 + (intensity * 0.1);
            for (let i = 0; i < combo; i++) {
                setTimeout(() => {
                    characterElement.animate([
                        { transform: 'scale(1)' },
                        { transform: `scale(${targetScale})` },
                        { transform: 'scale(1)' }
                    ], {
                        duration: 200,
                        easing: easings.easeInOutPower2
                    });
                }, i * 200);
            }
        }

        // 特殊感情状態をアクションで表現
        this.controller.performAction({ action: 'excited', intensity: 1.0 });

        // モード終了タイマー
        setTimeout(() => {
            this.specialEffectState.isInSpecialMode = false;
        }, duration);
    }
    
    /**
     * 特殊演出トリガー判定
     */
    checkSpecialEffectTriggers(intensity: number, mood: string, timestamp: number) {
        // 極高インテンシティでの特殊演出
        if (intensity > 0.95 && mood === 'intense') {
            this.triggerUltimateEffect(intensity, timestamp);
        }
        
        // BPMシンクロ特殊演出
        if (this.shouldTriggerBPMSync()) {
            this.triggerBPMSyncEffect(intensity, timestamp);
        }
    }
    
    /**
     * 究極演出トリガー
     */
    triggerUltimateEffect(_intensity: number, timestamp: number) {
        if (this.lastUltimateEffect > 0 && timestamp - this.lastUltimateEffect < 10000) return; // 10秒クールダウン

        this.lastUltimateEffect = timestamp;

        // 複合アクション実行（利用可能なアクションを使用）
        const ultimateSequence = ['excited', 'surprised', 'happy', 'normalSpin'];
        ultimateSequence.forEach((action, index) => {
            setTimeout(() => {
                this.controller.performAction({ action, intensity: 1.0 });
            }, index * 300);
        });

        // 視覚的極限演出 using Web Animations API
        const characterElement = document.getElementById('tokchan-display');
        if (characterElement) {
            animateSequence(characterElement, [
                {
                    keyframes: [
                        { transform: 'scale(1) rotate(0deg)', filter: 'brightness(1) saturate(1) hue-rotate(0deg)' },
                        { transform: 'scale(1.5) rotate(360deg)', filter: 'brightness(1.8) saturate(1.6) hue-rotate(180deg)' }
                    ],
                    options: {
                        duration: 1000,
                        easing: easings.easeOutPower2,
                        fill: 'forwards'
                    }
                },
                {
                    keyframes: [
                        { transform: 'scale(1.5) rotate(360deg)', filter: 'brightness(1.8) saturate(1.6) hue-rotate(180deg)' },
                        { transform: 'scale(1) rotate(0deg)', filter: 'brightness(1) saturate(1) hue-rotate(0deg)' }
                    ],
                    options: {
                        duration: 2000,
                        easing: easings.elasticOut,
                        fill: 'forwards'
                    }
                }
            ]);
        }
    }
    
    /**
     * BPMシンクロ演出判定
     */
    shouldTriggerBPMSync() {
        // BPMパターンに基づく特殊演出判定ロジック
        return this.comboCounter >= 8;
    }
    
    /**
     * BPMシンクロ演出
     */
    triggerBPMSyncEffect(_intensity: number, _timestamp: number) {
        // BPMに完全同期した演出を実装
    }
    
    /**
     * カスタムパターン登録
     */
    registerCustomPattern(name: string, pattern: MoodPattern) {
        this.customPatterns.set(name, pattern);
    }
    
    /**
     * 演出システム有効化
     */
    activate() {
        this.isActive = true;
        logger.info(`🎭 CharacterBeatEffects.activate() called - isActive is now: ${this.isActive}`);
    }
    
    /**
     * 演出システム無効化
     */
    deactivate() {
        this.isActive = false;
    }
    
    /**
     * 統計情報取得
     */
    getStats() {
        return {
            isActive: this.isActive,
            comboCounter: this.comboCounter,
            isInSpecialMode: this.specialEffectState.isInSpecialMode,
            currentIntensity: this.getSmoothedIntensity(),
            effectCounts: {
                kick: this.lastEffectTime.kick > 0 ? 1 : 0,
                emotion: this.lastEffectTime.emotion > 0 ? 1 : 0,
                action: this.lastEffectTime.action > 0 ? 1 : 0
            }
        };
    }
    
    /**
     * システムリセット
     */
    reset() {
        this.comboCounter = 0;
        this.intensityHistory = [];
        this.specialEffectState.isInSpecialMode = false;
        this.lastEffectTime = { kick: 0, emotion: 0, action: 0, scale: 0 };
    }
}

// グローバル公開
if (typeof window !== 'undefined') {
    window.CharacterBeatEffects = CharacterBeatEffects;
    logger.info('🎭 CharacterBeatEffects module loaded and registered to window');
}

// ES module export
export { CharacterBeatEffects };
